import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import type { CreateShippingPolicyModalProps, FormData } from './types';
import type { ExpressShippingFieldErrors } from './types';
import { CountryCode } from '../../../../models/Currency';
import ExpressShippingFields from './ExpressShippingFields';
import { getDefaultExpressShipping, supportsExpressShipping, validateExpressShipping } from './expressShipping';
import type { ExpressShippingPolicy } from '../../../../infrastructure/ShippingClient';

const CreateShippingPolicyModal: React.FC<CreateShippingPolicyModalProps> = ({ show, onHide, onSubmit, isLoading }) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('' as CountryCode);
  const [shippingDuration, setShippingDuration] = useState({
    minDay: 5,
    maxDay: 10,
  });
  const [expressShipping, setExpressShipping] = useState<ExpressShippingPolicy | undefined>();
  const [errors, setErrors] = useState<{ countryCode?: string; minDay?: string; maxDay?: string }>({});
  const [expressErrors, setExpressErrors] = useState<ExpressShippingFieldErrors>({});

  const handleShippingDurationChange = (field: 'minDay' | 'maxDay', value: number) => {
    setShippingDuration((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { countryCode?: string; minDay?: string; maxDay?: string } = {};

    if (!selectedCountry) {
      newErrors.countryCode = 'Country is required';
    }

    if (shippingDuration.minDay < 1) {
      newErrors.minDay = 'Minimum days must be at least 1';
    }

    if (shippingDuration.maxDay < 1) {
      newErrors.maxDay = 'Maximum days must be at least 1';
    }

    if (shippingDuration.minDay > shippingDuration.maxDay) {
      newErrors.maxDay = 'Maximum days must be greater than or equal to minimum days';
    }

    const newExpressErrors = expressShipping?.enabled ? validateExpressShipping(expressShipping) : {};
    setErrors(newErrors);
    setExpressErrors(newExpressErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newExpressErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Create FormData with only required fields for API
    const formData: FormData = {
      countryCode: selectedCountry,
      shippingDuration,
      ...(expressShipping?.enabled ? { expressShipping } : {}),
    };

    onSubmit(formData);
  };

  const handleClose = () => {
    setSelectedCountry('' as CountryCode);
    setShippingDuration({ minDay: 5, maxDay: 10 });
    setExpressShipping(undefined);
    setErrors({});
    setExpressErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Shipping Policy</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="create-shipping-policy-country">
                <Form.Label>Country *</Form.Label>
                <Form.Select
                  value={selectedCountry}
                  onChange={(e) => {
                    const countryCode = e.target.value as CountryCode;
                    setSelectedCountry(countryCode);
                    setExpressShipping(
                      countryCode && supportsExpressShipping(countryCode)
                        ? getDefaultExpressShipping(countryCode)
                        : undefined,
                    );
                    setExpressErrors({});
                    if (errors.countryCode) {
                      const { countryCode, ...rest } = errors;
                      setErrors(rest);
                    }
                  }}
                  isInvalid={Boolean(errors.countryCode)}
                >
                  <option value="">Select a country</option>
                  {Object.values(CountryCode).map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.countryCode}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="create-shipping-policy-min-days">
                <Form.Label>Minimum Delivery Days *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={shippingDuration.minDay}
                  onChange={(e) => handleShippingDurationChange('minDay', parseInt(e.target.value) || 1)}
                  isInvalid={Boolean(errors.minDay)}
                />
                <Form.Control.Feedback type="invalid">{errors.minDay}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="create-shipping-policy-max-days">
                <Form.Label>Maximum Delivery Days *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={shippingDuration.maxDay}
                  onChange={(e) => handleShippingDurationChange('maxDay', parseInt(e.target.value) || 1)}
                  isInvalid={Boolean(errors.maxDay)}
                />
                <Form.Control.Feedback type="invalid">{errors.maxDay}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {expressShipping && (
            <ExpressShippingFields
              countryCode={selectedCountry}
              value={expressShipping}
              errors={expressErrors}
              onChange={(value) => {
                setExpressShipping(value);
                setExpressErrors({});
              }}
            />
          )}

          {(Object.keys(errors).length > 0 || Object.keys(expressErrors).length > 0) && (
            <Alert variant="danger">Please fix the errors above before submitting.</Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Policy'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateShippingPolicyModal;
